const AVATAR_WIDTH = 80;
const AVATAR_BOTTOM = 20;

export class Avatar {
  readonly el: HTMLImageElement;

  // _lerpX: current rendered X position — advanced each frame by update().
  // _targetX: destination X — set by lerpToX(). The gap closes by lerpSpeed each frame.
  private _lerpX: number = 0;
  private _targetX: number = 0;
  private readonly halfWidth = AVATAR_WIDTH / 2;

  constructor(url: string, initialContainerWidth: number) {
    this._lerpX = initialContainerWidth / 2;
    this._targetX = initialContainerWidth / 2;

    this.el = document.createElement("img");
    this.el.src = url;
    this.el.className = "io-avatar";
    this.el.draggable = false;
    Object.assign(this.el.style, {
      position: "absolute",
      bottom: `${AVATAR_BOTTOM}px`,
      width: `${AVATAR_WIDTH}px`,
      height: "auto",
      marginLeft: `-${this.halfWidth}px`,
      left: `${this._lerpX}px`,
      pointerEvents: "none",
      userSelect: "none",
      zIndex: "10",
    });
  }

  // Current rendered X (lerpX, not target).
  // Used for catch radius checks — reflects where the avatar actually is on screen.
  get centerX(): number {
    return this._lerpX;
  }

  // Set lerp target. Used for:
  //   desktop hover enter  → cursor X
  //   desktop hover leave  → container center
  //   mobile auto-catch    → column X on icon spawn
  lerpToX(x: number, containerWidth: number): void {
    this._targetX = Math.max(this.halfWidth, Math.min(containerWidth - this.halfWidth, x));
  }

  // Advance lerp by one frame. Must be called every tick.
  // lerpSpeed is a fraction of remaining distance closed per frame (0–1).
  //   0.15 @ 60fps ≈ 90% of distance in ~15 frames (~250ms) — desktop hover feel
  //   0.12 @ 60fps ≈ 98% of max column distance in ~40 frames — mobile always-catch
  update(lerpSpeed: number): void {
    this._lerpX += (this._targetX - this._lerpX) * lerpSpeed;
    this.el.style.left = `${this._lerpX}px`;
  }

  // Instant position + target reset. Used for:
  //   initialization, and hard reset when loop stops (viewport exit).
  setInstant(x: number, containerWidth: number): void {
    const clamped = Math.max(this.halfWidth, Math.min(containerWidth - this.halfWidth, x));
    this._lerpX = clamped;
    this._targetX = clamped;
    this.el.style.left = `${clamped}px`;
  }

  topEdge(containerHeight: number): number {
    return containerHeight - AVATAR_BOTTOM - (this.el.offsetHeight || 56);
  }

  pulse(isSpecial: boolean): void {
    this.el.classList.remove("io-avatar--pulse", "io-avatar--pulse-special");
    void this.el.offsetWidth;
    const cls = isSpecial ? "io-avatar--pulse-special" : "io-avatar--pulse";
    this.el.classList.add(cls);
    this.el.addEventListener("animationend", () => this.el.classList.remove(cls), { once: true });
  }
}
